import React, { useState } from 'react';
import {
  LayoutDashboard,
  FileText,
  Receipt,
  Users,
  Wallet,
  Tag,
  Unplug,
  BarChart3,
  Settings,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Shield,
  KeyRound,
  UserPlus,
  Crown,
  FileCheck,
  RotateCcw,
  Home,
  LogOut,
  PenLine,
  Repeat,
  ClipboardList,
  X,
} from 'lucide-react';
import MonarchLogo from './analytics/MonarchLogo';
import { getAdminRoleLabel } from '../utils/adminRoleLabel';
import { canSeeRenewals } from '../utils/renewalAccess';
import { staffRole } from '../utils/staffRole';
import './Sidebar.css';
import '../styles/analytics-theme.css';

const Sidebar = ({ activeTab, onTabChange, user, onLogout, onBackToMain, isMobileOpen, onMobileClose }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const menuItems = [
    {
      id: 'overview',
      label: 'Dashboard',
      icon: LayoutDashboard,
      description: 'Overview & Analytics',
      roles: ['admin', 'user', 'super_admin', 'finance_admin', 'credit_control', 'underwriting'],
    },
    {
      id: 'transactions',
      label: 'All Transactions',
      icon: Receipt,
      description: 'All agent transactions (incl. backfilled)',
      roles: ['admin', 'super_admin', 'finance_admin'],
    },
    {
      id: 'agents-analytics',
      label: 'Agents',
      icon: Users,
      description: 'Agent business & commissions',
      roles: ['admin', 'super_admin', 'finance_admin'],
    },
    {
      id: 'commissions-payable',
      label: 'Commissions Payable',
      icon: Wallet,
      description: 'Track Commission Payments',
      roles: ['super_admin', 'finance_admin'],
    },
    {
      id: 'product-rates',
      label: 'Product Rates',
      icon: Tag,
      description: 'TPO & TOR pricing schedule',
      roles: ['super_admin'],
    },
    {
      id: 'comprehensive-issued',
      label: 'Comprehensive Issued',
      icon: Shield,
      description: 'COMP covers progress & Core status',
      roles: ['super_admin'],
    },
    {
      id: 'unsynchronized-policies',
      label: 'Unsynchronized Policies',
      icon: Unplug,
      description: 'Core submission reconciliation',
      roles: ['admin', 'super_admin', 'finance_admin'],
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: FileCheck,
      description: 'Export datasets to CSV/Excel',
      roles: ['admin', 'super_admin', 'finance_admin'],
    },
    {
      id: 'paid-no-cert',
      label: 'Cert recovery',
      icon: RotateCcw,
      description: 'Paid but certificate not issued',
      roles: ['admin', 'super_admin', 'finance_admin'],
    },
    {
      id: 'kyc',
      label: 'KYC Verification',
      icon: Shield,
      description: 'Review Agent KYCs',
      roles: ['admin', 'super_admin'],
    },
    {
      id: 'self-registered-agents',
      label: 'Self-Registered Agents',
      icon: ClipboardList,
      description: 'Sign Up accounts and KYC review',
      roles: ['admin', 'super_admin', 'credit_control'],
    },
    {
      id: 'revenue',
      label: 'Revenue',
      icon: BarChart3,
      description: 'Financial Analytics',
      roles: ['admin', 'super_admin', 'finance_admin'],
    },
    {
      id: 'password-management',
      label: 'Password Reset',
      icon: KeyRound,
      description: 'Reset Agent Passwords',
      roles: ['admin', 'super_admin'],
    },
    {
      id: 'create-from-tq',
      label: 'Create from TQ',
      icon: UserPlus,
      description: 'Create agents from TQ (Oracle) DB',
      roles: ['admin', 'super_admin', 'credit_control'],
    },
    {
      id: 'users',
      label: 'Users',
      icon: Users,
      description: 'User Management',
      roles: ['admin', 'super_admin', 'credit_control'],
    },
    {
      id: 'super-admin-management',
      label: 'Super Admin',
      icon: Crown,
      description: 'Manage Super Admins',
      roles: ['super_admin'],
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      description: 'Account & Preferences',
      roles: ['admin', 'user', 'super_admin', 'finance_admin', 'credit_control'],
    },
    {
      id: 'underwriting-renewals',
      label: 'Renewals',
      icon: RefreshCw,
      description: 'TPO and TOR renewals on your book',
      roles: ['underwriting'],
      renewalsPilot: true,
    },
    {
      id: 'underwriting-issue-cover',
      label: 'Issue Cover for Agent',
      icon: PenLine,
      description: 'Link cover to agent and submit to Core',
      roles: ['underwriting'],
    },
    {
      id: 'underwriting-covers-issued',
      label: 'Covers Issued',
      icon: ClipboardList,
      description: 'Covers you have issued',
      roles: ['underwriting'],
    },
    {
      id: 'underwriting-extend-policy',
      label: 'Buy/Extend Policy',
      icon: Repeat,
      description: 'Buy or extend policy for an agent',
      roles: ['underwriting'],
    },
    {
      id: 'underwriting-comprehensive',
      label: 'Comprehensive',
      icon: Shield,
      description: 'COMP purchase flow (1 month · 30%)',
      roles: ['underwriting', 'super_admin'],
    },
    {
      id: 'underwriting-comp-continuations',
      label: 'Extend Comprehensive',
      icon: ClipboardList,
      description: 'COMP extensions due → requests@',
      roles: ['underwriting', 'super_admin'],
    },
    {
      id: 'underwriting-issued-covers-admin',
      label: 'Underwriting Issued Covers',
      icon: FileText,
      description: 'All covers issued by underwriters',
      roles: ['admin', 'super_admin', 'finance_admin', 'credit_control'],
    },
  ].filter((item) => {
    const role = staffRole(user) || 'admin';
    if (item.roles && !item.roles.includes(role)) return false;
    if (item.renewalsPilot && !canSeeRenewals(user)) return false;
    return true;
  });

  const handleTabClick = (tabId) => {
    onTabChange(tabId);
    if (onMobileClose) onMobileClose();
  };

  return (
    <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <MonarchLogo variant="mark" className="sidebar-monarch-logo" />
          {!isCollapsed && (
            <div className="logo-text">
              <h2>Monarch</h2>
              <span>Insurance Portal</span>
            </div>
          )}
        </div>

        <button
          className="collapse-btn"
          onClick={() => setIsCollapsed(!isCollapsed)}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>

        <button
          className="mobile-close-btn"
          onClick={onMobileClose}
          title="Close menu"
          aria-label="Close menu"
        >
          <X size={18} strokeWidth={2} />
        </button>
      </div>

      <nav className="sidebar-nav">
        <ul className="nav-list">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.id} className="nav-item">
                <button
                  className={`nav-link ${activeTab === item.id ? 'active' : ''}`}
                  onClick={() => handleTabClick(item.id)}
                  title={isCollapsed ? item.label : ''}
                >
                  <span className="nav-icon lucide-icon">
                    <Icon size={18} strokeWidth={2} />
                  </span>
                  {!isCollapsed && (
                    <div className="nav-content">
                      <span className="nav-label">{item.label}</span>
                      <span className="nav-description">{item.description}</span>
                    </div>
                  )}
                  {activeTab === item.id && <div className="active-indicator" />}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-refresh-wrap">
          <button
            className="sidebar-refresh-btn"
            onClick={() => window.location.reload()}
            title="Refresh dashboard"
          >
            <RefreshCw size={16} strokeWidth={2} />
            {!isCollapsed && <span>Refresh</span>}
          </button>
        </div>

        <div className="user-section">
          <div className="user-avatar sidebar-user-avatar">
            {user?.username?.charAt(0).toUpperCase() || 'A'}
          </div>
          {!isCollapsed && (
            <div className="user-info">
              <span className="user-name">{user?.username || 'Admin'}</span>
              <span className="sidebar-role-badge">{getAdminRoleLabel(user?.role)}</span>
            </div>
          )}
        </div>

        <div className="footer-actions">
          <button
            className="back-to-main-btn"
            onClick={onBackToMain}
            title={isCollapsed ? 'Back to Main App' : ''}
          >
            <Home size={16} strokeWidth={2} />
            {!isCollapsed && <span>Main App</span>}
          </button>
          <button
            className="logout-btn"
            onClick={onLogout}
            title={isCollapsed ? 'Logout' : ''}
          >
            <LogOut size={16} strokeWidth={2} />
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
